import type { ReactNode } from "react";
import { useNavigate } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { ContextMenu as C } from "radix-ui";
import { REACTION_EMOJIS, type Conversation, type UserSummary } from "@atrium/shared";
import { api, errorMessage } from "../../lib/api";
import { cn } from "../../lib/cn";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
  ContextMenuTrigger,
} from "../../components/ui/ContextMenu";
import { MenuItem, MenuSeparator } from "../../components/ui/Menu";
import { confirm } from "../../components/ui/Dialog";
import { toast } from "../../components/ui/Toast";
import { IconBlock, IconCopy, IconIdCard, IconLogout, IconMention, IconMessages, IconSmile, IconTimer, IconUser } from "../../components/icons";
import { copyText } from "../posts/usePostActions";
import { insertIntoComposer } from "./composerBus";
import type { MessageAction, UserModeration } from "./types";

/*
 * Message and user menus. A message's actions are built once as plain
 * descriptors and rendered both in the toolbar's "⋯" dropdown and in the
 * right-click / long-press context menu, so the two never drift apart.
 */

export type ActionSections = MessageAction[][];

export async function copyWithToast(text: string, what = "Copied") {
  try {
    await copyText(text);
    toast.show(what);
  } catch {
    toast.error("Couldn't copy");
  }
}

/** Items for the toolbar's "⋯" dropdown. */
export function DropdownActionItems({ sections }: { sections: ActionSections }) {
  const visible = sections.filter((s) => s.length);
  return (
    <>
      {visible.map((s, i) => (
        <SectionFragment key={i} first={i === 0} separator={<MenuSeparator />}>
          {s.map((a) => (
            <MenuItem key={a.key} icon={a.icon} danger={a.danger} hint={a.hint} onSelect={a.onSelect}>
              {a.label}
            </MenuItem>
          ))}
        </SectionFragment>
      ))}
    </>
  );
}

function SectionFragment({ first, separator, children }: { first: boolean; separator: ReactNode; children: ReactNode }) {
  return (
    <>
      {!first && separator}
      {children}
    </>
  );
}

/** One-click reactions across the top of a message's context menu. */
function QuickReactionRow({ emojis, mine, onReact }: { emojis: string[]; mine: Set<string>; onReact: (e: string) => void }) {
  return (
    <C.Group className="flex items-center justify-between gap-0.5 px-0.5 pb-1" aria-label="Quick reactions">
      {emojis.map((e) => (
        <C.Item
          key={e}
          onSelect={() => onReact(e)}
          aria-label={`${mine.has(e) ? "Remove reaction" : "React with"} ${e}`}
          className={cn(
            "flex size-8 shrink-0 cursor-pointer select-none items-center justify-center rounded-md text-xl outline-none data-[highlighted]:bg-surface-2",
            mine.has(e) && "bg-accent-soft",
          )}
        >
          {e}
        </C.Item>
      ))}
    </C.Group>
  );
}

/** The context menu for a message: quick reactions, then the same actions as the "⋯" menu. */
export function MessageContextMenu({
  children,
  disabled,
  sections,
  react,
  onOpenChange,
}: {
  children: ReactNode;
  disabled?: boolean;
  sections: ActionSections;
  /** Present when the viewer may react. */
  react?: { quick: string[]; mine: Set<string>; onReact: (emoji: string) => void };
  onOpenChange?: (open: boolean) => void;
}) {
  const visible = sections.filter((s) => s.length);
  return (
    <ContextMenu onOpenChange={onOpenChange} modal={false}>
      <ContextMenuTrigger asChild disabled={disabled}>
        {children}
      </ContextMenuTrigger>
      {/* Narrow screens: never wider than the space beside the pointer. */}
      <ContextMenuContent className="!min-w-0 !max-w-[min(18rem,var(--radix-context-menu-content-available-width))]">
        {react && (
          <>
            <QuickReactionRow emojis={react.quick} mine={react.mine} onReact={react.onReact} />
            <ContextMenuSub>
              <ContextMenuSubTrigger icon={IconSmile}>Add reaction</ContextMenuSubTrigger>
              <ContextMenuSubContent className="min-w-0 max-w-none">
                <div className="grid grid-cols-6 gap-0.5" aria-label="Reactions">
                  {REACTION_EMOJIS.map((e) => (
                    <C.Item
                      key={e}
                      onSelect={() => react.onReact(e)}
                      aria-label={`React with ${e}`}
                      className={cn("flex size-9 cursor-pointer select-none items-center justify-center rounded-md text-xl outline-none data-[highlighted]:bg-surface-2", react.mine.has(e) && "bg-accent-soft")}
                    >
                      {e}
                    </C.Item>
                  ))}
                </div>
              </ContextMenuSubContent>
            </ContextMenuSub>
            {visible.length > 0 && <ContextMenuSeparator />}
          </>
        )}
        {visible.map((s, i) => (
          <SectionFragment key={i} first={i === 0} separator={<ContextMenuSeparator />}>
            {s.map((a) => (
              <ContextMenuItem key={a.key} icon={a.icon} danger={a.danger} hint={a.hint} onSelect={a.onSelect}>
                {a.label}
              </ContextMenuItem>
            ))}
          </SectionFragment>
        ))}
      </ContextMenuContent>
    </ContextMenu>
  );
}

const TIMEOUTS = [
  { m: 1, l: "1 minute" },
  { m: 5, l: "5 minutes" },
  { m: 10, l: "10 minutes" },
  { m: 60, l: "1 hour" },
  { m: 60 * 24, l: "1 day" },
  { m: 60 * 24 * 7, l: "1 week" },
];

/**
 * Right-click / long-press menu on a message author's name or avatar:
 * profile, message, mention, copy, and moderation when permitted.
 */
export function UserContextMenu({
  user,
  meId,
  canMention,
  moderation,
  extra,
  children,
}: {
  user: UserSummary;
  meId: string;
  canMention: boolean;
  moderation?: UserModeration;
  /** Items for where the menu is used (friend actions…), shown after Message/Mention. */
  extra?: ReactNode;
  children: ReactNode;
}) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const self = user.id === meId;
  const mod = moderation && !self && user.id !== moderation.ownerId ? moderation : undefined;
  const timedOutUntil = mod?.timedOutUntil?.(user.id) ?? null;
  const timedOut = !!timedOutUntil && new Date(timedOutUntil) > new Date();

  const message = async () => {
    try {
      const c = await api.post<Conversation>("/api/conversations", { userIds: [user.id] });
      navigate(`/messages/${c.id}`);
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };
  const timeout = async (minutes: number) => {
    if (!mod) return;
    try {
      await api.put(`/api/communities/${mod.communityId}/members/${user.id}/timeout`, { minutes });
      void qc.invalidateQueries({ queryKey: ["members", mod.communityId] });
      toast.show(minutes ? `${user.displayName} is timed out` : "Time-out removed");
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };
  const remove = async (kind: "kick" | "ban") => {
    if (!mod) return;
    const ok = await confirm({
      title: `${kind === "kick" ? "Kick" : "Ban"} ${user.displayName}?`,
      description: kind === "kick" ? "They can rejoin with an invite, or directly if the community is public." : "They'll be removed and won't be able to rejoin.",
      confirmLabel: kind === "kick" ? "Kick" : "Ban",
      danger: true,
    });
    if (!ok) return;
    try {
      if (kind === "kick") await api.del(`/api/communities/${mod.communityId}/members/${user.id}`);
      else await api.put(`/api/communities/${mod.communityId}/bans/${user.id}`, { reason: "" });
      void qc.invalidateQueries({ queryKey: ["members", mod.communityId] });
      toast.show(kind === "kick" ? `${user.displayName} was kicked` : `${user.displayName} was banned`);
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  return (
    <ContextMenu modal={false}>
      <ContextMenuTrigger
        asChild
        // A long press on the name opens this menu, not the message's.
        onPointerDown={(e) => e.pointerType === "touch" && e.stopPropagation()}
      >
        {children}
      </ContextMenuTrigger>
      <ContextMenuContent>
        <ContextMenuItem icon={IconUser} onSelect={() => navigate(`/u/${user.username}`)}>
          Profile
        </ContextMenuItem>
        {!self && (
          <ContextMenuItem icon={IconMessages} onSelect={() => void message()}>
            Message
          </ContextMenuItem>
        )}
        {canMention && (
          <ContextMenuItem icon={IconMention} onSelect={() => insertIntoComposer(`@${user.username} `)}>
            Mention
          </ContextMenuItem>
        )}
        {extra}
        <ContextMenuSeparator />
        <ContextMenuItem icon={IconCopy} onSelect={() => void copyWithToast(user.username)}>
          Copy username
        </ContextMenuItem>
        <ContextMenuItem icon={IconIdCard} onSelect={() => void copyWithToast(user.id)}>
          Copy user ID
        </ContextMenuItem>
        {mod && (mod.timeout || mod.kick || mod.ban) && (
          <>
            <ContextMenuSeparator />
            {mod.timeout &&
              (timedOut ? (
                <ContextMenuItem icon={IconTimer} onSelect={() => void timeout(0)}>
                  Remove time-out
                </ContextMenuItem>
              ) : (
                <ContextMenuSub>
                  <ContextMenuSubTrigger icon={IconTimer}>Time out</ContextMenuSubTrigger>
                  <ContextMenuSubContent>
                    {TIMEOUTS.map((t) => (
                      <ContextMenuItem key={t.m} onSelect={() => void timeout(t.m)}>
                        {t.l}
                      </ContextMenuItem>
                    ))}
                  </ContextMenuSubContent>
                </ContextMenuSub>
              ))}
            {mod.kick && (
              <ContextMenuItem icon={IconLogout} danger onSelect={() => void remove("kick")}>
                Kick
              </ContextMenuItem>
            )}
            {mod.ban && (
              <ContextMenuItem icon={IconBlock} danger onSelect={() => void remove("ban")}>
                Ban
              </ContextMenuItem>
            )}
          </>
        )}
      </ContextMenuContent>
    </ContextMenu>
  );
}
